// ChallengePage.js
import React, { useState, useEffect, useMemo } from 'react';
import './ChallengePage.css';
import { useNavigate } from 'react-router-dom';
import { db } from './firebaseConfig';
import {
	collection,
	getDocs,
	getDoc,
	setDoc,
	doc,
	increment,
	updateDoc,
	deleteDoc,
	query,
	orderBy,
	runTransaction,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Select from 'react-select';
import countries from 'world-countries';

function ChallengePage() {
	const navigate = useNavigate();
	const [quotes, setQuotes] = useState([]);
	const [currentQuote, setCurrentQuote] = useState(null);
	const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0); // For display in corners.
	const [quotePatternIndex, setQuotePatternIndex] = useState(0); // Pointer into our difficulty pattern.
	const [selectedOption, setSelectedOption] = useState('');
	const [timeLeft, setTimeLeft] = useState(15);
	const [gameOver, setGameOver] = useState(false);
	const [usedQuoteIds, setUsedQuoteIds] = useState([]);
	// New state: show instructions page before starting the game.
	const [showInstructions, setShowInstructions] = useState(true);

	// New state to track session timing and aggregate results.
	const [gameStartTime, setGameStartTime] = useState(Date.now());
	const [sessionRecorded, setSessionRecorded] = useState(false);
	const [recordedTimeTaken, setRecordedTimeTaken] = useState(null);
	const [levelsReached, setLevelsReached] = useState(0);
	const [playerPercentile, setPlayerPercentile] = useState(null);
	const [eligibleForLeaderboard, setEligibleForLeaderboard] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Define the difficulty selection pattern using useMemo to keep it stable.
	const pattern = useMemo(() => ['easy', 'easy', 'easy', 'medium', 'easy', 'medium', 'hard', 'medium', 'expert'], []);

	// Map each difficulty to a numeric rank (lower number = easier).
	const difficultyRank = {
		easy: 1,
		medium: 2,
		hard: 3,
		expert: 4,
	};

	// Hardcoded options: 12 Nolan movies + one extra option.
	const options = [
		'Following',
		'Memento',
		'Insomnia',
		'Batman Begins',
		'The Prestige',
		'The Dark Knight',
		'Inception',
		'The Dark Knight Rises',
		'Interstellar',
		'Dunkirk',
		'Tenet',
		'Oppenheimer',
		'Not from a Nolan movie',
	];

	const countryOptions = countries.map((c) => ({
		label: c.name.common,
		value: c.cca2,
		flag: `https://flagcdn.com/w40/${c.cca2.toLowerCase()}.png`,
	}));

	const customSingleValue = ({ data }) => (
		<div style={{ display: 'flex', alignItems: 'center' }}>
			<img src={data.flag} alt="" width="20" style={{ marginRight: '8px' }} />
			{data.label}
		</div>
	);

	const customOption = (props) => {
		const { data, innerRef, innerProps } = props;
		return (
			<div ref={innerRef} {...innerProps} style={{ padding: '0.4rem', display: 'flex', alignItems: 'center' }}>
				<img src={data.flag} alt="" width="20" style={{ marginRight: '8px' }} />
				{data.label}
			</div>
		);
	};

	// Fetch all quotes from Firestore on mount.
	useEffect(() => {
		async function fetchQuotes() {
			try {
				const quotesRef = collection(db, 'nolan_challenge_quotes');
				const querySnapshot = await getDocs(quotesRef);
				const quotesArray = querySnapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}));
				setQuotes(quotesArray);
				const startingDifficulty = pattern[0];
				const available = quotesArray.filter((q) => q.difficulty === startingDifficulty);
				if (available.length > 0) {
					const randomQuote = available[Math.floor(Math.random() * available.length)];
					setCurrentQuote(randomQuote);
				} else if (quotesArray.length > 0) {
					const randomQuote = quotesArray[Math.floor(Math.random() * quotesArray.length)];
					setCurrentQuote(randomQuote);
				}
				setGameStartTime(Date.now());
			} catch (error) {
				console.error('Error fetching quotes:', error);
			}
		}
		fetchQuotes();
	}, [pattern]);

	// Countdown timer effect.
	useEffect(() => {
		if (showInstructions) return; // Do not start timer until instructions are hidden
		if (gameOver) return;
		if (timeLeft <= 0) {
			setGameOver(true);
			return;
		}
		const timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
		return () => clearTimeout(timerId);
	}, [timeLeft, gameOver, showInstructions]);

	// When game is over, record session data and update global aggregate stats.
	useEffect(() => {
		if (gameOver && !sessionRecorded) {
			const timeTaken = (Date.now() - gameStartTime) / 1000; // in seconds
			setRecordedTimeTaken(timeTaken);
			const auth = getAuth();
			const playerId = auth.currentUser ? auth.currentUser.uid : 'anonymous';
			const todayStr = new Date().toISOString().split('T')[0];
			// Compute levels reached: if all quotes have been used then it's the total number,
			// otherwise it's currentQuoteIndex + 1.
			const computedLevels = usedQuoteIds.length === quotes.length ? quotes.length : currentQuoteIndex + 1;
			setLevelsReached(computedLevels);
			// Use computedLevels in the custom document ID.
			const customId = `${todayStr}-lvl-${computedLevels}-${Date.now()}`;

			const sessionData = {
				timeTaken,
				playerId,
				timestamp: new Date().toISOString(),
				levelsReached: computedLevels,
			};
			const dayDocRef = doc(db, 'nolan_challenge_sessions', todayStr);

			const sessionDocRef = doc(collection(dayDocRef, 'sessions'), customId);
			runTransaction(db, async (transaction) => {
				const dayDoc = await transaction.get(dayDocRef);
				if (!dayDoc.exists()) {
					transaction.set(dayDocRef, { totalSessions: 1 });
				} else {
					transaction.update(dayDocRef, { totalSessions: increment(1) });
				}
				transaction.set(sessionDocRef, sessionData);
			})
				.then(() => setSessionRecorded(true))
				.catch((error) => console.error('Error saving session:', error));

			// Update global aggregate stats.
			const aggregateDocRef = doc(db, 'nolan_challenge_aggregate_stats', 'global');
			getDoc(aggregateDocRef)
				.then((docSnap) => {
					let data = docSnap.exists()
						? docSnap.data()
						: {
								totalGamesPlayed: 0,
								levelDistribution: {},
								bestTimeForLevel: {},
								maxLevelAchieved: 0,
						  };

					// Ensure properties exist if they are missing
					data.totalGamesPlayed = (data.totalGamesPlayed || 0) + 1;
					if (!data.levelDistribution) {
						data.levelDistribution = {};
					}
					if (!data.bestTimeForLevel) {
						data.bestTimeForLevel = {};
					}

					const levelKey = String(computedLevels);
					data.levelDistribution[levelKey] = (data.levelDistribution[levelKey] || 0) + 1;
					// Update bestTimeForLevel if current timeTaken is lower.
					if (!data.bestTimeForLevel[levelKey] || timeTaken < data.bestTimeForLevel[levelKey]) {
						data.bestTimeForLevel[levelKey] = timeTaken;
					}
					if (computedLevels > data.maxLevelAchieved) {
						data.maxLevelAchieved = computedLevels;
					}
					data.lastUpdated = new Date().toISOString();
					return setDoc(aggregateDocRef, data, { merge: true });
				})
				.catch((error) => console.error('Error updating aggregate stats:', error));
		}
	}, [gameOver, sessionRecorded, gameStartTime, currentQuoteIndex, usedQuoteIds, quotes]);

	// After session is recorded, fetch aggregate stats to calculate percentile.
	useEffect(() => {
		if (gameOver && sessionRecorded && levelsReached > 0) {
			const aggregateDocRef = doc(db, 'nolan_challenge_aggregate_stats', 'global');
			getDoc(aggregateDocRef)
				.then((docSnap) => {
					if (docSnap.exists()) {
						const data = docSnap.data();
						let sumLower = 0;
						for (let i = 1; i < levelsReached; i++) {
							sumLower += data.levelDistribution[String(i)] || 0;
						}
						const percentile =
							data.totalGamesPlayed > 0 ? Math.floor((sumLower / data.totalGamesPlayed) * 100) : 0;
						setPlayerPercentile(percentile);
					}
				})
				.catch((error) => console.error('Error fetching aggregate stats:', error));
		}
	}, [gameOver, sessionRecorded, levelsReached]);

	useEffect(() => {
		if (gameOver && sessionRecorded && levelsReached > 0) {
			const aggregateDocRef = doc(db, 'nolan_challenge_aggregate_stats', 'global');
			getDoc(aggregateDocRef)
				.then((docSnap) => {
					if (docSnap.exists()) {
						const data = docSnap.data();
						let sumLower = 0;
						for (let i = 1; i < levelsReached; i++) {
							sumLower += data.levelDistribution[String(i)] || 0;
						}
						const percentile =
							data.totalGamesPlayed > 0 ? Math.floor((sumLower / data.totalGamesPlayed) * 100) : 0;
						setPlayerPercentile(percentile);
					}
				})
				.catch((error) => console.error('Error fetching aggregate stats:', error));

			// 🔍 Check if this score is within the top 50 leaderboard entries
			const entriesRef = collection(db, 'challenge_leaderboard/Nolan/entries');
			getDocs(entriesRef).then((querySnapshot) => {
				const allEntries = querySnapshot.docs.map((doc) => doc.data());

				// Sort entries by levelsReached DESC, timeTaken ASC
				// Fetch the top 50 sorted entries
				const sorted = allEntries
					.sort((a, b) => {
						if (b.levelsReached !== a.levelsReached) {
							return b.levelsReached - a.levelsReached;
						}
						return a.timeTaken - b.timeTaken;
					})
					.slice(0, 50); // Limit to top 50

				// Check if current session qualifies for top 50
				if (sorted.length < 50) {
					setEligibleForLeaderboard(true);
				} else {
					const worstEntry = sorted[sorted.length - 1];
					if (
						levelsReached > worstEntry.levelsReached ||
						(levelsReached === worstEntry.levelsReached && recordedTimeTaken < worstEntry.timeTaken)
					) {
						setEligibleForLeaderboard(true);
					} else {
						setEligibleForLeaderboard(false);
					}
				}
			});
		}
	}, [gameOver, sessionRecorded, levelsReached, recordedTimeTaken]);

	// Helper: Randomly pick one quote from an array.
	const randomPick = (arr) => arr[Math.floor(Math.random() * arr.length)];

	// Helper: Select a new quote based on the current pattern and unused quote IDs.
	const selectQuote = (patternIndex, usedIds) => {
		const desiredDifficulty = pattern[patternIndex];
		const candidates = quotes.filter((q) => !usedIds.includes(q.id));
		if (candidates.length === 0) return null;
		let matching = candidates.filter((q) => q.difficulty === desiredDifficulty);
		if (matching.length > 0) {
			return randomPick(matching);
		}
		const desiredRank = difficultyRank[desiredDifficulty];
		let easierOrEqual = candidates.filter((q) => difficultyRank[q.difficulty] <= desiredRank);
		if (easierOrEqual.length > 0) {
			return randomPick(easierOrEqual);
		}
		return randomPick(candidates);
	};

	const handleOptionClick = (option) => {
		setSelectedOption(option);
	};

	const handleSubmit = async () => {
		if (!selectedOption) return;

		const correctAnswer = options.includes(currentQuote.movie) ? currentQuote.movie : 'Not from a Nolan movie';

		const isCorrect = selectedOption === correctAnswer;

		try {
			const quoteRef = doc(db, 'nolan_challenge_quotes', currentQuote.id);

			await updateDoc(quoteRef, {
				timesGuessed: increment(1),
				timesGuessedCorrect: isCorrect ? increment(1) : increment(0),
			});
		} catch (error) {
			console.error('Error updating quote stats:', error);
		}

		if (isCorrect) {
			const newUsedQuoteIds = [...usedQuoteIds, currentQuote.id];
			setUsedQuoteIds(newUsedQuoteIds);
			setCurrentQuoteIndex((prev) => prev + 1);
			const newPatternIndex = (quotePatternIndex + 1) % pattern.length;
			setQuotePatternIndex(newPatternIndex);
			const newQuote = selectQuote(newPatternIndex, newUsedQuoteIds);
			if (newQuote) {
				setCurrentQuote(newQuote);
				setSelectedOption('');
				setTimeLeft(15);
			} else {
				setGameOver(true);
			}
		} else {
			setGameOver(true);
		}
	};

	const [username, setUsername] = useState('');
	const [country, setCountry] = useState('');
	const [leaderboardSubmitted, setLeaderboardSubmitted] = useState(false);

	const handleLeaderboardSubmit = async () => {
		if (!username || !country || leaderboardSubmitted || isSubmitting) return;
		setIsSubmitting(true);
		const auth = getAuth();
		const userId = auth.currentUser ? auth.currentUser.uid : 'anonymous';
		const entry = {
			username,
			country,
			timeTaken: recordedTimeTaken,
			levelsReached,
			userId,
			timestamp: new Date().toISOString(),
		};

		try {
			// Create a new reference for the entry
			const newEntryRef = doc(collection(db, 'challenge_leaderboard/Nolan/entries'), `${Date.now()}-${username}`);

			// First save the new entry
			await setDoc(newEntryRef, entry);

			// Then get all entries and handle the top 50 logic separately
			const entriesRef = collection(db, 'challenge_leaderboard/Nolan/entries');
			const q = query(entriesRef, orderBy('levelsReached', 'desc'), orderBy('timeTaken', 'asc'));
			const querySnapshot = await getDocs(q);
			const allEntries = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

			// Sort entries
			allEntries.sort((a, b) => {
				if (a.levelsReached !== b.levelsReached) {
					return b.levelsReached - a.levelsReached; // Descending by levels
				}
				return a.timeTaken - b.timeTaken; // Ascending by time
			});

			// If we have more than 50 entries, delete extras
			if (allEntries.length > 50) {
				const entriesToRemove = allEntries.slice(50); // Entries beyond the top 50

				for (const entryToRemove of entriesToRemove) {
					if (entryToRemove && entryToRemove.id) {
						try {
							const entryRef = doc(db, 'challenge_leaderboard/Nolan/entries', entryToRemove.id);
							await deleteDoc(entryRef);
						} catch (err) {
							console.error('Error deleting entry:', err, 'Entry:', entryToRemove);
						}
					} else {
						console.warn('Skipped deletion for malformed or missing entry:', entryToRemove);
					}
				}
			}

			setLeaderboardSubmitted(true);
			navigate('/leaderboard/Nolan');
		} catch (error) {
			console.error('Error submitting leaderboard entry:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// ★★★ Instructions Page ★★★
	// Render the instructions before the game starts using the same challenge-card container.
	if (showInstructions) {
		return (
			<div className="challenge-page-container">
				<div className="challenge-card instruction-card">
					<div className="instruction-content">
						<p>Are you a Christopher Nolan fan?</p>
						<p>Are you really?</p>
						<p>Let's put your Nolan movies knowledge to the test.</p>
						<ol>
							<li>You'll have 15 seconds to guess each quote.</li>
							<li>
								Some of the quotes are <b>not</b> from Nolan's movies, so choose wisely, my friend.
							</li>
							<li>Top 50 results are recorded on the leaderboard.</li>
							<li>Good luck!</li>
						</ol>
						<button
							className="start-button"
							onClick={() => {
								setShowInstructions(false);
								setGameStartTime(Date.now()); // Reset game start time when challenge begins
							}}
						>
							Start Challenge
						</button>
					</div>
					<div className="instruction-home-button-container">
						<button onClick={() => navigate('/')} className="instruction-home-button">
							Home
						</button>
					</div>
				</div>
			</div>
		);
	}

	if (!currentQuote) {
		return <div>Loading...</div>;
	}
	if (gameOver) {
		return (
			<div className="challenge-over-container">
				<h1 className="challenge-over-title">Game Over</h1>
				<div className="challenge-summary-card">
					<p>
						🎯 <strong>Levels Reached:</strong> {levelsReached}
					</p>
					<p>
						⏱ <strong>Time Taken:</strong> {recordedTimeTaken?.toFixed(1)} seconds
					</p>
					{playerPercentile !== null && (
						<p>
							📊 <strong>You beat</strong> {playerPercentile}% of players
						</p>
					)}

					{eligibleForLeaderboard && !leaderboardSubmitted && (
						<div className="challenge-leaderboard-entry">
							<p>🌟 You made the Top 50! Enter your name and country:</p>
							<input
								className="challenge-input"
								type="text"
								placeholder="Username"
								value={username}
								onChange={(e) => setUsername(e.target.value)}
							/>
							<Select
								className="challenge-country-select"
								classNamePrefix="rs"
								options={countryOptions}
								onChange={(selected) => setCountry(selected.label)}
								isSearchable
								placeholder="Select your country..."
								components={{ SingleValue: customSingleValue, Option: customOption }}
							/>

							<button className="challenge-submit-button" onClick={handleLeaderboardSubmit}>
								Submit to Leaderboard
							</button>
						</div>
					)}

					{leaderboardSubmitted && (
						<p className="challenge-confirmation-message">✅ Leaderboard entry submitted!</p>
					)}

					{!eligibleForLeaderboard && (
						<div className="challenge-action-buttons">
							<button className="play-again-button" onClick={() => window.location.reload()}>
								Play Again
							</button>
							<button className="home-button" onClick={() => navigate('/')}>
								Home
							</button>
						</div>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="challenge-page-container">
			<div className="challenge-card">
				{/* Poker card–style corner numbers showing the current attempt count */}
				<div className="card-corner top-left">{currentQuoteIndex + 1}</div>
				<div className="card-corner bottom-right">{currentQuoteIndex + 1}</div>
				<div className="sketch-area">
					<img src={currentQuote.sketch} alt="Sketch" className="challenge-sketch-image" />
				</div>
				<div className="quote-area">
					<p className="challenge-quote-text">{currentQuote.text}</p>
				</div>
				<div className="audio-area">
					<audio
						controls
						src={currentQuote.audio}
						className="audio-player"
						controlsList="nodownload noplaybackrate"
					/>
				</div>
				{/* Options container with 13 buttons */}
				<div className="options-container">
					{options.map((option, index) => (
						<button
							key={index}
							className={`option-button ${selectedOption === option ? 'selected' : ''}`}
							onClick={() => handleOptionClick(option)}
							disabled={gameOver}
						>
							{option}
						</button>
					))}
				</div>
				{/* Submit button */}
				<div className="submit-container">
					<button className="submit-button" onClick={handleSubmit} disabled={gameOver || !selectedOption}>
						Submit
					</button>
				</div>
				<div className="timer-display">Time left: {timeLeft}s</div>
			</div>
		</div>
	);
}

export default ChallengePage;
